import { getSuperTokensApiService } from './superTokensApiService'
import { backendApi } from './backendApiService'

/**
 * One column of a keyset/cursor sort. Ordered lists of these drive
 * `dbApi.getAll(..., { keyset })`; the final column must be unique (a PK)
 * so paging strictly advances. See DatabaseApiService.getAll.
 */
export type KeysetColumn = { column: string; dir: 'asc' | 'desc' }

class DatabaseApiService {
  private api = getSuperTokensApiService()

  // In-flight GET dedupe. Many parts of the app independently fetch the
  // same small queries during a single page load (default property,
  // debug + maintenance settings, role permissions, etc.) — these
  // duplicate hits add up to 1-1.5s of waterfall. If the same GET URL
  // is already pending, return the existing promise instead of firing
  // another request. The map clears on settle, so each "next" caller
  // after the request completes hits PostgREST normally.
  private inflight = new Map<string, Promise<any>>()

  async get(endpoint: string, params?: Record<string, string>): Promise<any> {
    // Build the dedupe key from endpoint + params so callers that pass
    // params separately still share the same in-flight slot.
    const key = params && Object.keys(params).length > 0
      ? `${endpoint}|${new URLSearchParams(params).toString()}`
      : endpoint
    const existing = this.inflight.get(key)
    if (existing) return existing
    const p = this.api.get(endpoint, params).finally(() => {
      this.inflight.delete(key)
    })
    this.inflight.set(key, p)
    return p
  }

  /**
   * Count-only query against PostgREST. Returns just the total row
   * count for the given filter — no rows transferred, just the
   * Content-Range response header. Use when a KPI / pagination total
   * is needed but the actual data isn't (e.g. "11,201 Veranstaltungen"
   * in a card). Costs roughly one round-trip + a few bytes regardless
   * of how many rows match.
   *
   * Append filters via the endpoint query string, e.g.
   *   `await dbApi.getCount('/events?status=eq.definite')`
   */
  async getCount(endpoint: string): Promise<number> {
    const sep = endpoint.includes('?') ? '&' : '?'
    const url = `${endpoint}${sep}limit=1&select=id`
    try {
      const { headers } = await this.api.getWithResponse(url, undefined, { headers: { Prefer: 'count=exact' } })
      const cr = headers.get('content-range') || headers.get('Content-Range')
      if (!cr) return 0
      const slash = cr.lastIndexOf('/')
      if (slash < 0) return 0
      const n = Number(cr.slice(slash + 1))
      return Number.isFinite(n) ? n : 0
    } catch (e) {
      console.warn('[dbApi.getCount] failed for', endpoint, e)
      return 0
    }
  }

  /**
   * Paginated GET that fetches all rows beyond PostgREST's PGRST_DB_MAX_ROWS limit (1000).
   *
   * Two strategies, selected via `opts`:
   *
   * OFFSET (default) — `limit&offset` batches:
   *   1. Fire the first batch WITHOUT requesting count. `count=exact`
   *      forces PostgREST to run `COUNT(*)` over the full filter, which
   *      on large tables (e.g. pms_blocks) can add hundreds of ms to
   *      every page load — even when the result set fits in one batch.
   *   2. If the first batch is under `batchSize`, return it. We never
   *      paid the count cost.
   *   3. If the first batch IS full, we likely have more rows. Fire a
   *      cheap count probe (`limit=1` with `count=exact`) and then fan
   *      out the remaining offsets in parallel.
   *   Small/medium queries (the common case) cost exactly 1 request.
   *   Large queries cost 1 + 1 count + N-1 parallel. This is the right
   *   default for date/property-bounded sets that fit in a few batches.
   *
   * KEYSET (opt-in via `{ keyset: [...] }`) — cursor batches:
   *   Deep OFFSET degrades on large unbounded sets — Postgres must sort
   *   and then skip N rows for every late batch (offset 8000-11000 on the
   *   full events table balloons to 8-13s). Keyset instead orders by a
   *   unique key and pages with `WHERE key < lastRow`, so every batch
   *   costs the same regardless of depth. It is sequential (batch N+1
   *   needs the last row of batch N), trading OFFSET's parallelism for a
   *   flat per-batch cost — a net win once the deep-offset tail dominates.
   *   Use for the hot, large, unbounded fetch (the full events list);
   *   keep OFFSET for the small bounded ones.
   *
   * @param opts - a number is treated as `batchSize` (legacy signature);
   *   an object may carry `{ batchSize?, keyset? }`. `keyset` is an ordered
   *   list of `{ column, dir }` whose LAST entry must be a unique column
   *   (e.g. the PK) so paging always makes strict progress.
   */
  async getAll(
    endpoint: string,
    opts: number | { batchSize?: number; keyset?: KeysetColumn[] } = {},
  ): Promise<any[]> {
    const batchSize = typeof opts === 'number' ? opts : (opts.batchSize ?? 1000)
    const keyset = typeof opts === 'number' ? undefined : opts.keyset
    if (keyset && keyset.length > 0) {
      return this.getAllKeyset(endpoint, batchSize, keyset)
    }

    const separator = endpoint.includes('?') ? '&' : '?'

    // First batch — no count. Most queries fit here and never need pagination.
    const firstUrl = `${endpoint}${separator}limit=${batchSize}&offset=0`
    const firstBatch: any[] = await this.api.get(firstUrl)
    if (!Array.isArray(firstBatch) || firstBatch.length === 0) return []
    if (firstBatch.length < batchSize) return firstBatch

    // Full batch → there's probably more. Do a count probe to enable parallel fan-out.
    let totalCount: number | null = null
    try {
      const probeUrl = `${endpoint}${separator}limit=1&offset=0`
      const res = await this.api.getWithResponse(probeUrl, undefined, { headers: { Prefer: 'count=exact' } })
      const cr = res.headers?.get?.('content-range') || res.headers?.['content-range']
      if (cr) {
        const slash = String(cr).lastIndexOf('/')
        const n = slash >= 0 ? Number(String(cr).slice(slash + 1)) : NaN
        if (!Number.isNaN(n)) totalCount = n
      }
    } catch { /* helper not available — fall through to serial walk */ }

    if (totalCount === null) {
      // No count header → serial walk (correct but slower for very large sets).
      const all = [...firstBatch]
      let offset = batchSize
      while (true) {
        const batch = await this.api.get(`${endpoint}${separator}limit=${batchSize}&offset=${offset}`)
        if (!Array.isArray(batch) || batch.length === 0) break
        all.push(...batch)
        if (batch.length < batchSize) break
        offset += batchSize
      }
      return all
    }

    // Parallel fan-out — issue all remaining batch requests at once.
    const remaining: Promise<any[]>[] = []
    for (let offset = batchSize; offset < totalCount; offset += batchSize) {
      remaining.push(this.api.get(`${endpoint}${separator}limit=${batchSize}&offset=${offset}`))
    }
    const rest = await Promise.all(remaining)
    const all = [...firstBatch]
    for (const part of rest) {
      if (Array.isArray(part)) all.push(...part)
    }
    return all
  }

  /**
   * Keyset/cursor pagination — see getAll's KEYSET section. Walks batches
   * sequentially, ordering by `keyset` and, after the first batch, adding a
   * `WHERE (key) < lastRow` filter so Postgres never pays the deep-OFFSET
   * skip cost. The final keyset column must be unique (PK) so each batch
   * strictly advances past the previous one — no dupes, no gaps, no loop.
   */
  private async getAllKeyset(endpoint: string, batchSize: number, keyset: KeysetColumn[]): Promise<any[]> {
    // The emitted ORDER BY must match the WHERE comparison, so strip any
    // inline `order=` the caller passed and drive ordering from `keyset`.
    const base = this.stripQueryParam(endpoint, 'order')
    const separator = base.includes('?') ? '&' : '?'
    const orderStr = keyset.map((k) => `${k.column}.${k.dir}`).join(',')

    const all: any[] = []
    let cursor: any[] | null = null
    // Backstop against a non-shrinking server response; far above any real
    // table (10k batches × 1000 rows = 10M) so it never trips in practice.
    const maxBatches = 10000
    for (let i = 0; i < maxBatches; i++) {
      let url = `${base}${separator}order=${orderStr}&limit=${batchSize}`
      if (cursor) url += `&${this.buildKeysetFilter(keyset, cursor)}`
      const batch = await this.api.get(url)
      if (!Array.isArray(batch) || batch.length === 0) break
      all.push(...batch)
      if (batch.length < batchSize) break
      const last = batch[batch.length - 1]
      const next = keyset.map((k) => last[k.column])
      // If any key column is missing on the last row we can't build a safe
      // cursor — stop rather than risk skipped rows or an unbounded loop.
      if (next.some((v) => v === undefined || v === null)) break
      cursor = next
    }
    return all
  }

  /**
   * Build the PostgREST filter for "rows after `cursor`" in composite-key
   * order. PostgREST has no row-value `(a,b) < (x,y)` operator, so emulate
   * it with or/and:
   *   or=( k0 OP v0,
   *        and(k0.eq.v0, k1 OP v1),
   *        and(k0.eq.v0, k1.eq.v1, k2 OP v2), ... )
   * where OP is `lt` for a desc column and `gt` for asc.
   */
  private buildKeysetFilter(keyset: KeysetColumn[], cursor: any[]): string {
    const terms: string[] = []
    for (let i = 0; i < keyset.length; i++) {
      const parts: string[] = []
      for (let j = 0; j < i; j++) {
        parts.push(`${keyset[j].column}.eq.${cursor[j]}`)
      }
      const op = keyset[i].dir === 'desc' ? 'lt' : 'gt'
      parts.push(`${keyset[i].column}.${op}.${cursor[i]}`)
      terms.push(parts.length > 1 ? `and(${parts.join(',')})` : parts[0])
    }
    return `or=(${terms.join(',')})`
  }

  /** Remove a single query param (e.g. `order`) from an endpoint URL. */
  private stripQueryParam(endpoint: string, param: string): string {
    const qIdx = endpoint.indexOf('?')
    if (qIdx < 0) return endpoint
    const path = endpoint.slice(0, qIdx)
    const kept = endpoint
      .slice(qIdx + 1)
      .split('&')
      .filter((p) => p && p.split('=')[0] !== param)
    return kept.length ? `${path}?${kept.join('&')}` : path
  }

  async post(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    return this.api.post(endpoint, data, options)
  }

  async patch(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    return this.api.patch(endpoint, data, options)
  }

  async put(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    return this.api.put(endpoint, data, options)
  }

  async delete(endpoint: string, options?: RequestInit): Promise<any> {
    return this.api.delete(endpoint, options)
  }

  // Helper method for building query strings
  buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value))
      }
    })
    return searchParams.toString()
  }

  // ═══════════════════════════════════════════════════════════════
  // Space Bookings API (new endpoints)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Book a space for an event (creates per-day booking slots)
   * Can auto-assign best-fit space or book a specific one
   */
  async bookSpace(data: {
    event_id: number
    property_id: number
    start_date: string
    end_date: string
    start_time?: string
    end_time?: string
    space_id?: number
    session_id?: number
    min_capacity?: number
    setup_type_id?: number
    expected_pax?: number
    notes?: string
    replace_existing?: boolean
  }): Promise<{
    status: string
    bookings: Array<{ id: number; event_id: number; space_id: number; start_time: string; end_time: string }>
    space_id: number
    space_name: string
    days: number
    auto_assigned: boolean
  }> {
    return backendApi.request('/api/space-bookings/book', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Atomically replace the session-scoped booking group containing one row.
   *
   * `preserve_existing_range: true` tells the backend to keep the locked
   * group's own start/end instead of the range in this payload — the correct
   * mode for a PURE ROOM MOVE, where only `space_id` changes. Use it rather
   * than deriving times client-side: booking timestamps arrive from PostgREST
   * as `timestamptz`, so slicing the wire string can hand the backend a time
   * in the wrong offset and silently rewrite the booking's wall-clock hours
   * (the drift T-2580 fixed on the backend side).
   *
   * `start_date`/`end_date` are still required by the route's field check even
   * in preserve mode; the service overwrites both from the locked rows.
   */
  async replaceSessionBooking(data: {
    old_booking_id: number
    event_id: number
    property_id: number
    start_date: string
    end_date: string
    start_time?: string
    end_time?: string
    space_id?: number
    session_id?: number
    setup_type_id?: number
    expected_pax?: number
    notes?: string
    custom_name?: string
    custom_price?: number
    preserve_existing_range?: boolean
  }): Promise<{
    status: string
    bookings: Array<{ id: number; event_id: number; space_id: number; start_time: string; end_time: string }>
    space_id: number
    space_name: string
    days: number
    auto_assigned: boolean
  }> {
    return backendApi.request('/api/space-bookings/replace-session', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a space booking (partial update)
   */
  async updateSpaceBooking(id: number, data: {
    space_id?: number
    start_time?: string
    end_time?: string
    expected_pax?: number
    notes?: string
    status?: string
    setup_type_id?: number
    custom_name?: string
    custom_price?: number
  }): Promise<{
    status: string
    booking: { id: number; event_id: number; space_id: number; start_time: string; end_time: string }
  }> {
    return backendApi.request(`/api/space-bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Create a space-less room REQUEST (status 'requested') — a time window + pax
   * + setup with no space. The hotel later assigns a space via assignSpaceToRequest.
   */
  async requestSpace(data: {
    event_id: number
    start_date: string
    end_date: string
    start_time: string // HH:MM
    end_time: string   // HH:MM
    setup_type_id?: number | null
    expected_pax?: number | null
    session_id?: number | null
    notes?: string | null
  }): Promise<{ status: string; booking: { id: number; event_id: number } }> {
    return backendApi.request('/api/space-bookings/request', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Assign a space to a space-less request → status 'suggested'. 409 on a clash
   * (SPACE_UNAVAILABLE) or when the row is no longer an open request.
   */
  async assignSpaceToRequest(id: number, spaceId: number): Promise<{
    status: string
    booking: { id: number; event_id: number; space_id: number }
  }> {
    return backendApi.request(`/api/space-bookings/${id}/assign-space`, {
      method: 'POST',
      body: JSON.stringify({ space_id: spaceId }),
    })
  }

  /**
   * Delete a space booking
   */
  async deleteSpaceBooking(id: number): Promise<{
    status: string
    booking_id: number
  }> {
    return backendApi.request(`/api/space-bookings/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Check available spaces for a date range and capacity
   */
  async checkSpaceAvailability(data: {
    property_id: number
    start_date: string
    end_date: string
    start_time?: string
    end_time?: string
    min_capacity?: number
    exclude_booking_id?: number
  }): Promise<any> {
    return backendApi.request('/api/space-bookings/check-availability', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get all space bookings for an event
   */
  async getEventSpaceBookings(eventId: number): Promise<any[]> {
    return backendApi.request(`/api/space-bookings/event/${eventId}`, {
      method: 'GET',
    })
  }

  /**
   * Get all conflicting/overlapping bookings
   */
  async getSpaceBookingConflicts(): Promise<any[]> {
    return backendApi.request('/api/space-bookings/conflicts', {
      method: 'GET',
    })
  }
}

let dbApiInstance: DatabaseApiService | null = null

export function useDbApi(): DatabaseApiService {
  if (!dbApiInstance) {
    dbApiInstance = new DatabaseApiService()
  }
  return dbApiInstance
}

export const dbApi = useDbApi()
